import { ArrowDownRight, MapPin, Download } from "lucide-react";
import { COMPANY } from "@/lib/data";

const BROCHURE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/brochure`;

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-ink text-bone grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/9213609/pexels-photo-9213609.jpeg"
          alt="Aerial view of highway construction"
          className="w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/55 to-ink" />
      </div>

      {/* Marquee */}
      <div className="absolute top-24 md:top-28 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="marquee-track font-heading font-black text-[22vw] md:text-[14vw] leading-none tracking-tighter text-bone/[0.04] whitespace-nowrap">
          <span className="px-8">S.V.P.INFRA · EST. 2010 · BENGALURU ·</span>
          <span className="px-8">S.V.P.INFRA · EST. 2010 · BENGALURU ·</span>
        </div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24 min-h-screen flex flex-col">
        {/* Top meta row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12 md:mb-20">
          <span data-testid="hero-meta-location" className="font-mono text-[11px] md:text-xs tracking-[0.25em] uppercase text-bone/70 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Bengaluru, Karnataka — India
          </span>
          <span className="font-mono text-[11px] md:text-xs tracking-[0.25em] uppercase text-bone/70">
            [ Civil · External Development · Roadworks ]
          </span>
        </div>

        {/* Main heading */}
        <div className="stagger flex-1 flex flex-col justify-end">
          <span data-testid="hero-eyebrow" className="tech-rule mb-6 md:mb-8 text-rust">
            Building Since {COMPANY.established}
          </span>
          <h1
            data-testid="hero-heading"
            className="font-heading font-black uppercase leading-[0.85] tracking-tighter text-[15vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[8.2vw] text-bone"
          >
            Building<br />
            <span className="text-rust">Infrastructure</span><br />
            With <span className="italic font-medium tracking-tight">precision.</span>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <p
              data-testid="hero-tagline"
              className="md:col-span-6 text-base md:text-lg text-bone/80 leading-relaxed max-w-xl"
            >
              S.V.P.INFRA delivers civil and external development works for India's
              most discerning developers — Phoenix, Hiranandani, SAP, Wipro, BMRCL —
              with integrity, innovation, and excellence at every kerb-line.
            </p>

            <div className="md:col-span-6 grid grid-cols-3 gap-px bg-bone/15 border border-bone/15">
              <Stat label="Established" value="2010" />
              <Stat label="Marquee Clients" value="7+" />
              <Stat label="Asphalt Plant" value="Owned" />
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-cta-primary"
              href="#contact"
              className="inline-flex items-center gap-3 bg-rust text-bone px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover-shift border border-rust"
            >
              Request a Quote <ArrowDownRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#projects"
              className="inline-flex items-center gap-3 border border-bone/40 text-bone px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-bone hover:text-ink transition-colors"
            >
              View Projects
            </a>
            <a
              data-testid="hero-cta-brochure"
              href={BROCHURE_URL}
              download="SVP-INFRA-Brochure.pdf"
              className="inline-flex items-center gap-3 border border-bone/40 text-bone px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-bone hover:text-ink transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={2.5} /> Download Brochure
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-ink p-5 md:p-6">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-bone/55 mb-2">
        {label}
      </div>
      <div className="font-heading font-black text-2xl md:text-3xl tracking-tight text-bone">
        {value}
      </div>
    </div>
  );
}
